// store/useLabelingStore.js
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import {
    ZOOM_STEP,
    clampZoom,
    fitToViewport,
    panViewport,
    zoomAtPoint,
    zoomToActualSize,
} from '../utils/canvasViewport';

/**
 * Keep `currentClassId` pointing at a class that actually exists — the first
 * one by default. Drawing is impossible without a selected class, so the app
 * should never sit in a state where classes exist but none is picked.
 */
const withValidClass = (classes, currentClassId) => {
    if (!Array.isArray(classes) || classes.length === 0) return currentClassId;
    return classes.some((cls) => cls.id === currentClassId) ? currentClassId : classes[0].id;
};

const generateId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return uuidv4();
};

const useDetectionStore = create((set, get) => ({
    // State
    canvas: null,
    images: [],
    currentImageIndex: 0,
    currentClassId: "0",
    selectedAnnIndex: -1,
    startX: 0,
    startY: 0,
    rect: null,
    zoomLevel: 1,
    copiedBoxes: [],
    error: '',
    mode: 'select',
    polygonPoints: [],
    polygonObj: null,
    isDrawingPolygon: false,
    needsReload: true,
    classes: [],
    BASE_STROKE_WIDTH: 2,
    isCrosshairEnabled: true,

    // ===== VIEWPORT / VIEW OPTIONS =====
    // zoomLevel is the real screen-px-per-image-px scale, so a 96px image and a
    // 6000px image both open filling the panel instead of at their native size.
    showLabels: true,          // class-name chips drawn over each annotation
    showFill: false,           // tint box interiors (helps spot very small boxes)
    isViewportFitted: true,    // false once the user zooms/pans by hand

    // Timestamp of the last attempt to draw with no class defined. Drawing
    // hooks bump it and the UI highlights the Classes panel for a moment —
    // a nudge in the place the user has to act, instead of a modal alert that
    // has to be dismissed before every single attempt.
    classAttentionAt: 0,

    // ===== AI AUTO-LABEL STATE =====
    autoMode: false,
    autoSettings: {
        // 'myclasses' = detect ONLY the classes the user defined (mapped via each
        // class's aiAliases); 'smart' = detect everything; 'coco' = 80 COCO classes.
        // Defaults to myclasses so the AI fills the user's classes instead of
        // inventing new ones. Falls back to smart automatically when no class exists.
        detectMode: 'myclasses',  // 'smart' | 'myclasses' | 'coco'
        confidence: 0.25,
        iouThreshold: 0.45,       // NMS overlap passed to the detector
        autoOnNavigate: true,     // auto-label unlabeled images on navigation
        // Master-image guided mode: the boxes drawn on the master image define
        // which object types get labeled everywhere else.
        useMaster: false,
        similarity: 0.22,         // CLIP appearance gate; 0 = class restriction only
    },
    masterImageId: null,          // id of the image acting as the master reference

    // ===== DRAG & DROP STATE =====
    dragOver: false,
    activeDragClass: null,

    // Set drag over state
    setDragOver: (value) => set({ dragOver: value }),

    // Setters
    setCanvas: (canvas) => set({ canvas }),
    setImages: (images) => set({ images }),
    setClasses: (value) =>
        set({ classes: value, currentClassId: withValidClass(value, get().currentClassId) }),
    //   setCurrentImageIndex: (currentImageIndex) => set({ currentImageIndex }),
    addAdditionalClasses: (newClasses) =>
        set(state => {
            const classes = [...state.classes, ...newClasses];
            // a freshly created first class becomes the active one, so the user
            // can draw straight away instead of hunting for a selection step
            return { classes, currentClassId: withValidClass(classes, state.currentClassId) };
        }),
    updateClass: (updatedClass) =>
        set({
            classes: get().classes.map(c =>
                c.id === updatedClass.id ? { ...c, ...updatedClass } : c
            ),
        }),

    // setCurrentImageIndex: (currentImageIndex) => set({ currentImageIndex, needsReload: true }),
    setCurrentImageIndex: (value) => set({ currentImageIndex: value, needsReload: true }),
    setCurrentClassId: (currentClassId) => set({ currentClassId }),
    setSelectedAnnIndex: (selectedAnnIndex) => set({ selectedAnnIndex }),
    setStartX: (startX) => set({ startX }),
    setStartY: (startY) => set({ startY }),
    setRect: (rect) => set({ rect }),
    setZoomLevel: (zoomLevel) => set({ zoomLevel }),
    setCopiedBoxes: (copiedBoxes) => set({ copiedBoxes }),
    setError: (error) => set({ error }),
    setMode: (mode) => set({ mode }),
    setPolygonPoints: (polygonPoints) => set({ polygonPoints }),
    setPolygonObj: (polygonObj) => set({ polygonObj }),
    setIsDrawingPolygon: (isDrawingPolygon) => set({ isDrawingPolygon }),
    setNeedsReload: (needsReload) => set({ needsReload }),
    setCrosshairEnabled: (isCrosshairEnabled) => set({ isCrosshairEnabled }),
    setShowLabels: (showLabels) => set({ showLabels }),
    setShowFill: (showFill) => set({ showFill }),
    flagClassAttention: () => set({ classAttentionAt: Date.now() }),
    // Derived getters
    getCurrentImage: () => {
        const { images, currentImageIndex } = get();
        return images[currentImageIndex];
    },

    // Complex actions
    //   updateAnnotations: (newAnns) => {
    //     const { images, currentImageIndex } = get();
    //     const newImages = [...images];
    //     newImages[currentImageIndex].annotations = newAnns;
    //     set({ images: newImages });
    //   },

    updateAnnotations: (newAnns) =>
        set(state => {
            // direct mutation of nested data
            state.images[state.currentImageIndex].annotations = newAnns;

            // return nothing → Zustand keeps outer reference unchanged
            return {};
        }),
    updatePolygons: (newPolygons) =>
        set(state => {
            state.images[state.currentImageIndex].polygons = newPolygons;
            return {};
        }),

    /**
     * Backfill originalWidth/Height from the decoded bitmap when the record
     * arrived without them — every annotation coordinate is normalised against
     * these, so a missing pair turns box maths into NaN.
     * Mutates in place on purpose: a new images ref would re-trigger the loader.
     */
    ensureImageDimensions: (index, width, height) =>
        set(state => {
            const image = state.images[index];
            if (!image || (image.originalWidth && image.originalHeight)) return {};
            image.originalWidth = width;
            image.originalHeight = height;
            return {};
        }),

    // ===== AI AUTO-LABEL ACTIONS =====
    setAutoMode: (autoMode) => set({ autoMode }),
    setAutoSettings: (partial) =>
        set(state => ({ autoSettings: { ...state.autoSettings, ...partial } })),
    setMasterImageId: (masterImageId) => set({ masterImageId }),

    /**
     * Replace every AI suggestion on an image with `newAnns`, leaving manual
     * annotations untouched. Used when the confidence slider changes so the
     * suggestions already on screen are re-filtered from the retained raw
     * detections instead of re-running inference.
     */
    setAiAnnotations: (imageIndex, newAnns) =>
        set(state => {
            const newImages = state.images.map((img, i) =>
                i === imageIndex
                    ? {
                        ...img,
                        annotations: [
                            ...(img.annotations || []).filter(ann => !ann.isAI),
                            ...(Array.isArray(newAnns) ? newAnns : []),
                        ],
                    }
                    : img
            );
            return { images: newImages };
        }),

    /** Append AI-suggested annotations to a specific image. New images ref so the canvas reloads. */
    applyAiAnnotations: (imageIndex, newAnns) =>
        set(state => {
            if (!Array.isArray(newAnns) || newAnns.length === 0) return {};
            const newImages = state.images.map((img, i) =>
                i === imageIndex
                    ? { ...img, annotations: [...(img.annotations || []), ...newAnns] }
                    : img
            );
            return { images: newImages };
        }),

    /** Accept AI suggestions on an image: strip the isAI flag so boxes render as normal labels. */
    acceptAiAnnotations: (imageIndex) =>
        set(state => {
            const newImages = state.images.map((img, i) =>
                i === imageIndex
                    ? {
                        ...img,
                        annotations: (img.annotations || []).map(ann =>
                            ann.isAI ? { ...ann, isAI: false } : ann
                        ),
                    }
                    : img
            );
            return { images: newImages };
        }),

    /** Discard all unaccepted AI suggestions on an image. */
    discardAiAnnotations: (imageIndex) =>
        set(state => {
            const newImages = state.images.map((img, i) =>
                i === imageIndex
                    ? { ...img, annotations: (img.annotations || []).filter(ann => !ann.isAI) }
                    : img
            );
            return { images: newImages };
        }),

    /** Restore annotations and polygons (for undo/redo). Creates new images ref to trigger reload. */
    restoreAnnotationsAndPolygons: (annotations, polygons) =>
        set(state => {
            const idx = state.currentImageIndex;
            const newImages = state.images.map((img, i) =>
                i === idx
                    ? {
                        ...img,
                        annotations: JSON.parse(JSON.stringify(annotations)),
                        polygons: JSON.parse(JSON.stringify(polygons)),
                    }
                    : img
            );
            return { images: newImages };
        }),

    addImage: (newImage) => {
        const { images } = get();
        set({ images: [...images, newImage] });
    },
    addimages: (newImages) => {
        const { images } = get();
        set({ images: [...images, ...newImages] });
    },

    removeImage: (index) => {
        const { images } = get();
        const newImages = images.filter((_, i) => i !== index);
        set({ images: newImages });
    },

    // Navigation
    nextImage: () => {
        const { currentImageIndex, images } = get();
        if (currentImageIndex < images.length - 1) {
            set({
                currentImageIndex: currentImageIndex + 1,
                needsReload: true
            });
        }
    },

    prevImage: () => {
        const { currentImageIndex } = get();
        if (currentImageIndex > 0) {
            set({
                currentImageIndex: currentImageIndex - 1,
                needsReload: true
            });
        }
    },

    // ===== ZOOM / PAN =====
    // All of these go through canvasViewport so the image stays centred, the
    // zoom stays inside [MIN_ZOOM, MAX_ZOOM] and annotation strokes keep a
    // constant on-screen width.

    /** Scale the image to fill the panel. This is the default view for every image. */
    zoomToFit: () => {
        const { canvas, getCurrentImage, BASE_STROKE_WIDTH } = get();
        if (!canvas) return;
        const zoom = fitToViewport(canvas, getCurrentImage(), BASE_STROKE_WIDTH);
        set({ zoomLevel: zoom, isViewportFitted: true });
    },

    /** 1 image pixel per screen pixel, anchored on the viewport centre. */
    zoomToActual: () => {
        const { canvas, getCurrentImage, BASE_STROKE_WIDTH } = get();
        if (!canvas) return;
        const zoom = zoomToActualSize(canvas, getCurrentImage(), BASE_STROKE_WIDTH);
        set({ zoomLevel: zoom, isViewportFitted: false });
    },

    /** Zoom to an explicit factor, optionally pinning a screen point (wheel zoom). */
    setZoomTo: (value, point = null) => {
        const { canvas, getCurrentImage, BASE_STROKE_WIDTH } = get();
        if (!canvas) return;
        const zoom = zoomAtPoint(canvas, getCurrentImage(), clampZoom(value), point, BASE_STROKE_WIDTH);
        set({ zoomLevel: zoom, isViewportFitted: false });
    },

    zoomIn: () => get().setZoomTo(get().zoomLevel * ZOOM_STEP),
    zoomOut: () => get().setZoomTo(get().zoomLevel / ZOOM_STEP),

    /** Toolbar "reset" means "show me the whole image" — the useful reset here. */
    resetZoom: () => get().zoomToFit(),

    panBy: (deltaX, deltaY) => {
        const { canvas, getCurrentImage, isViewportFitted } = get();
        if (!canvas) return;
        panViewport(canvas, getCurrentImage(), deltaX, deltaY);
        // only touch state on the first pan — a set() per pointermove would
        // re-render every subscriber of this store on each frame
        if (isViewportFitted) set({ isViewportFitted: false });
    },

    // Polygon drawing
    startPolygon: () => {
        const { canvas, polygonObj } = get();
        set({
            mode: 'polygon',
            isDrawingPolygon: true,
            polygonPoints: []
        });

        if (polygonObj && canvas) {
            canvas.remove(polygonObj);
            set({ polygonObj: null });
        }
    },

    // Image upload handler
    // handleImageUpload: (files) => {
    //     const { images, setError, addImage } = get();

    //     if (files.length === 0) {
    //         setError('No files selected.');
    //         return;
    //     }

    //     const newImages = [];
    //     let loadedCount = 0;

    //     files.forEach((file) => {
    //         const reader = new FileReader();

    //         reader.onload = (event) => {
    //             const img = new Image();

    //             img.onload = () => {
    //                 newImages.push({
    //                     id: crypto.randomUUID(),
    //                     fileName: file.name,
    //                     file,
    //                     dataUrl: event.target.result,
    //                     originalWidth: img.width,
    //                     originalHeight: img.height,
    //                     scale: 1,
    //                     annotations: [],
    //                     polygons: [],
    //                 });

    //                 loadedCount++;
    //                 if (loadedCount === files.length) {
    //                     newImages.forEach(image => addImage(image));
    //                     if (images.length === 0 && newImages.length > 0) {
    //                         set({ currentImageIndex: 0 });
    //                     }
    //                     setError('');
    //                 }
    //             };

    //             img.onerror = () => setError(`Failed to load image: ${file.name}`);
    //             img.src = event.target.result;
    //         };

    //         reader.onerror = () => setError(`Error reading file: ${file.name}`);
    //         reader.readAsDataURL(file);
    //     });
    // },

    handleImageUpload: (files) => {
    const { images, setError, addImage } = get();

    if (files.length === 0) {
        setError('No files selected.');
        return;
    }

    const newImages = [];
    let loadedCount = 0;

    // Get existing file names for duplicate checking
    const existingFileNames = new Set(images.map(img => img.fileName));

    files.forEach((file) => {
        // Skip if file name already exists
        if (existingFileNames.has(file.name)) {
            loadedCount++; // Count as processed
            if (loadedCount === files.length) {
                if (newImages.length > 0) {
                    newImages.forEach(image => addImage(image));
                    if (images.length === 0 && newImages.length > 0) {
                        set({ currentImageIndex: 0 });
                    }
                }
                setError('');
            }
            return;
        }

        const reader = new FileReader();

        reader.onload = (event) => {
            const img = new Image();

            img.onload = () => {
                newImages.push({
                    id: generateId(),
                    fileName: file.name,
                    file,
                    dataUrl: event.target.result,
                    originalWidth: img.width,
                    originalHeight: img.height,
                    scale: 1,
                    annotations: [],
                    polygons: [],
                });

                loadedCount++;
                if (loadedCount === files.length) {
                    if (newImages.length > 0) {
                        newImages.forEach(image => addImage(image));
                        if (images.length === 0 && newImages.length > 0) {
                            set({ currentImageIndex: 0 });
                        }
                    }
                    setError('');
                }
            };

            img.onerror = () => setError(`Failed to load image: ${file.name}`);
            img.src = event.target.result;
        };

        reader.onerror = () => setError(`Error reading file: ${file.name}`);
        reader.readAsDataURL(file);
    });
},
    // Inside useClassificationStore definition
    resetAll: () => {
        set({
            // canvas: null,
            images: [],
            currentImageIndex: 0,
            currentClassId: "0",
            selectedAnnIndex: -1,
            startX: 0,
            startY: 0,
            rect: null,
            zoomLevel: 1,
            copiedBoxes: [],
            error: '',
            mode: 'select',
            polygonPoints: [],
            polygonObj: null,
            isDrawingPolygon: false,
            needsReload: true,
            classes: [],
            isCrosshairEnabled: true,
            showLabels: true,
            showFill: false,
            isViewportFitted: true,
            classAttentionAt: 0,
            autoMode: false,
            masterImageId: null,
        });
    },
}));

export default useDetectionStore;